import { useToasts } from "react-toast-notifications";
import { useSendMoneyContext } from "../../../../contexts/send-money";
import { useNavigate } from "react-router-dom";
import Button from "../../../../components/common/Button";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import restService from "../../../../services/restService";
import { ConfirmationModal } from "./components/confirmation-modal";

import Loader from "../../../../components/common/Loader";
import { ArrowLeft } from "react-feather";

export default function Review() {
  const { addToast } = useToasts();
  const navigate = useNavigate();

  const agent = useSelector((state) => state.auth?.user);
  const [commissionConfigurationId, setCommissionConfigurationId] =
    useState(null);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const {
    sendMoneyData: { receiver, disbursal_country, sender, destination_wallet },
    sendAmount,
    agencyCommissionAmount,
    agentCommissionAmount,
    charge,
    receiveAmount,
    amountToPay,
    rate,
    agencyCurrency,
    transferReason,
    setAgentCommission,
    setAgencyCommission,
    setCurrentStep,
    rateRecord,
    transactionCharge,
  } = useSendMoneyContext();

  const [isSubmitting, setSubmitting] = useState(false);

  const handleSendMoney = async () => {

    setSubmitting(true);
    addToast("Processing Transaction", {
      appearance: "info",
      autoDismiss: true,
      autoDismissTimeout: 3000,
    });

    if (!transferReason) {
      addToast("Please select a transfer reason", {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 5000,
      });

      setSubmitting(false);
      return;
    }

    let sendMoneyRequestData = {};
    // Use actual charge and rate ids from context
    sendMoneyRequestData.charge_id = transactionCharge?.id;
    sendMoneyRequestData.rate_id = rateRecord?.id;
    sendMoneyRequestData.sender_id = sender.id;
    sendMoneyRequestData.commission_configuration_id =
      commissionConfigurationId;
    sendMoneyRequestData.receiver_id = receiver.id;
    sendMoneyRequestData.receiver_wallet_id = destination_wallet.id;
    sendMoneyRequestData.sender_relationship =
      receiver.pivot?.sender_relationship;
    sendMoneyRequestData.transaction_purpose = transferReason;
    sendMoneyRequestData.transaction_type = "Mobile Money";
    sendMoneyRequestData.receive_amount = Number(receiveAmount);
    sendMoneyRequestData.send_amount = Number(sendAmount);
    sendMoneyRequestData.destination_currency =
      receiver?.country?.currency_code;
    sendMoneyRequestData.source_currency = agencyCurrency?.label;
    sendMoneyRequestData.rate = rate;
    sendMoneyRequestData.charge = Number(charge);
    sendMoneyRequestData.agent_id = agent.id;
    sendMoneyRequestData.destination_institution_id =
      destination_wallet?.destination_institution.id;

    
    try {
      const {
        data: { data },
      } = await restService.TransactionsApi.sendMoney(sendMoneyRequestData);

      setShowConfirmationModal(true);
      addToast("Successfully Transferred to Receiver Wallet", {
        appearance: "success",
        autoDismiss: true,
        autoDismissTimeout: 5000,
      });
    } catch ({ response }) {
      addToast(response?.data?.message ?? "Transfer failed", {
        appearance: "error",
        autoDismiss: true,
        autoDismissTimeout: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  async function getCommissionConfiguration() {
    try {
      const {
        data: { data },
      } =
        await restService.CommissionsConfigApi.getCommissionConfigurationByAgency(
          agent?.agency?.id
        );

      setAgencyCommission(data.data?.[0]?.agency_percentage);
      setAgentCommission(data.data?.[0]?.agent_percentage);
      setCommissionConfigurationId(data.data?.[0]?.id);
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    getCommissionConfiguration();
  }, []);

  return (
    <div className="container flex flex-col gap-8">
      <Button
        variant="outline"
        onClick={() => setCurrentStep((prev) => prev - 1)}
        className="h-8 w-8 p-0 rounded-full bg-white text-blue-600 border border-gray-200 hover:bg-gray-50 shadow-sm"
        aria-label="Back"
      >
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
          className="flex-shrink-0"
        >
          <line x1="19" y1="12" x2="5" y2="12"></line>
          <polyline points="12,19 5,12 12,5"></polyline>
        </svg>
      </Button>
      <div className="max-w-5xl mx-auto w-full flex flex-col gap-6">
        {isSubmitting ? <Loader message={"Please wait..."} /> : null}

        <h4 className="text-lg text-black font-bold">Review & Confirm</h4>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left: Details Card */}
          <div className="md:col-span-2 bg-white border border-gray-200 rounded-xl shadow-sm p-6 flex flex-col gap-6">
            {/* Exchange Rate */}
            <div>
              <h5 className="text-sm text-gray-900 font-semibold mb-3">Transaction Details</h5>
              <div className="divide-y divide-gray-200">
                <div className="flex items-center justify-between py-3">
                  <p className="text-sm text-gray-500">Exchange Rate</p>
                  <p className="text-sm text-gray-900">
                    {agencyCurrency?.symbol}1 = {disbursal_country?.currency_symbol} {rate}
                  </p>
                </div>

                <div className="flex items-start justify-between py-3">
                  <p className="text-sm text-gray-500">Sending From</p>
                  <div className="flex flex-col text-right">
                    <p className="text-sm text-gray-900 font-medium">
                      {sender?.firstname} {sender?.lastname}
                    </p>
                    <p className="text-xs text-gray-500">{sender?.msisdn}</p>
                  </div>
                </div>

                <div className="flex items-start justify-between py-3">
                  <p className="text-sm text-gray-500">To</p>
                  <div className="flex flex-col text-right">
                    <p className="text-sm text-gray-900 font-medium">
                      {receiver?.firstname} {receiver?.lastname}
                    </p>
                    <p className="text-xs text-gray-500">{receiver?.country?.name}</p>
                    <p className="text-xs text-gray-500">{destination_wallet?.destination_institution?.name}</p>
                    <p className="text-xs text-gray-500">{destination_wallet?.account_number}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between py-3">
                  <p className="text-sm text-gray-500">Receive Amount</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {disbursal_country?.currency_symbol}
                    {" "}
                    {Number(Math.sign(receiveAmount) < 0 ? 0 : receiveAmount).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Summary Card */}
          <div className="md:col-span-1 bg-white border border-gray-200 rounded-xl shadow-sm p-6 flex flex-col gap-4">
            <h5 className="text-sm text-gray-900 font-semibold">Amount Summary</h5>
            <ul className="flex flex-col divide-y divide-gray-200">
              <li className="flex items-center justify-between py-3">
                <p className="text-sm text-gray-500">Send Amount</p>
                <p className="text-sm text-gray-900 font-medium">
                  {agencyCurrency?.symbol} {Number(sendAmount || 0).toFixed(2)}
                </p>
              </li>
              <li className="flex items-center justify-between py-3">
                <p className="text-sm text-gray-500">Charges</p>
                <p className="text-sm text-gray-900 font-medium">
                  {agencyCurrency?.symbol} {Number(charge || 0).toFixed(2)}
                </p>
              </li>
              <li className="flex items-center justify-between py-3">
                <p className="text-sm text-gray-500">Agent Commission</p>
                <p className="text-sm text-gray-900 font-medium">
                  {agencyCurrency?.symbol} {Number(agentCommissionAmount || 0).toFixed(2)}
                </p>
              </li>
              <li className="flex items-center justify-between py-3">
                <p className="text-sm text-gray-500">Agency Commission</p>
                <p className="text-sm text-gray-900 font-medium">
                  {agencyCurrency?.symbol} {Number(agencyCommissionAmount || 0).toFixed(2)}
                </p>
              </li>
              <li className="flex items-center justify-between py-3">
                <p className="text-sm text-gray-500">Total Amount</p>
                <p className="text-2xl font-bold text-blue-600">
                  {agencyCurrency?.symbol} {Number(amountToPay || 0).toFixed(2)}
                </p>
              </li>
            </ul>

            <Button
              variant="primary"
              size="lg"
              disabled={!destination_wallet?.id || !sendAmount || !sender?.id || isSubmitting}
              onClick={handleSendMoney}
              className="w-full mt-2"
            >
              Send Money
            </Button>
          </div>
        </div>

        <ConfirmationModal
          show={showConfirmationModal}
          disbursal_country={disbursal_country}
          receiveAmount={receiveAmount}
          setShow={() => {
            setShowConfirmationModal(false);
            navigate("/transactions");
          }}
        />
      </div>
    </div>
  );
}
